import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { IndicatorLevel, RecapReport, RecapStatus, RecapWarning } from './TableRecordingsRow';

// Renders a ReCap v3 report from its language-neutral keys. The text assembly mirrors
// terraform/lambda/teamei/utils/recap_render.ts (used by the PDF) — keep the two in step.
// Block order is fixed by the implementation guide: header, status, reading key, three
// indicators, warnings (only when present), interpretation, note.

type Translate = (key: string, vars?: Record<string, string | number>) => string;

const renderReadingSentence = (tokens: string[], t: Translate): string => {
  const text = tokens
    .map((k) => t(`recap.reading.${k}`))
    .join(' ')
    .replace(/ ([,;])/g, '$1');
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
};

const renderWarningText = (w: RecapWarning, t: Translate): string =>
  w.withConcentration
    ? `${t(`recap.warning.${w.id}.text`)} ${t(`recap.warning.${w.id}.concentration`)}`
    : t(`recap.warning.${w.id}.text`);

/** Colours never replace the text label: every chip also carries its level. */
const LEVEL_COLOR: Record<IndicatorLevel, 'success' | 'warning' | 'error' | 'default'> = {
  high: 'success',
  medium: 'warning',
  low: 'error',
  na: 'default',
};

const STATUS_COLOR: Record<RecapStatus, 'success' | 'warning' | 'error' | 'default'> = {
  solido: 'success',
  medio: 'warning',
  rischio: 'error',
  na: 'default',
};

const chipSx = { fontWeight: 'bold', textTransform: 'uppercase', flexShrink: 0 } as const;

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" paddingTop={3} paddingBottom={1}>
    {children}
  </Typography>
);

const WarningBox: React.FC<{ warning: RecapWarning }> = ({ warning }) => {
  const { t } = useTranslation();
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
      <Typography variant="subtitle1" fontWeight="bold">
        {t(`recap.warning.${warning.id}.title`)}
        <Typography component="span" variant="subtitle2" color="text.secondary">
          {' · '}
          {t(`recap.warning.level.${warning.level}`)}
        </Typography>
      </Typography>
      <Typography variant="body2">{renderWarningText(warning, t)}</Typography>
    </Paper>
  );
};

export const RecapReportView: React.FC<{ report: RecapReport }> = ({ report }) => {
  const { t } = useTranslation();
  const [showAllWarnings, setShowAllWarnings] = React.useState(false);
  const dims = ['trust', 'safety', 'belonging'] as const;
  const mainWarnings = report.warnings.filter((w) => w.main);
  const otherWarnings = report.warnings.filter((w) => !w.main);

  return (
    <Box sx={{ maxWidth: 860 }}>
      {/* A · header */}
      <Typography variant="overline" color="primary" fontWeight="bold">
        {t('recap.header')} · {t('recap.main_title')}
      </Typography>

      {/* B · overall status */}
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" paddingTop={1}>
        <Chip
          label={t(`recap.status.${report.status}`)}
          color={STATUS_COLOR[report.status]}
          sx={{ ...chipSx, minWidth: 150 }}
        />
        {report.qualifier && (
          <Typography variant="subtitle1" fontWeight="bold">
            {t(`recap.qualifier.${report.qualifier}`)}
          </Typography>
        )}
      </Box>
      <Typography variant="h6" paddingTop={1}>
        {t(`recap.status_title.${report.status}`)}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {t(`recap.status_copy.${report.status}`)}
      </Typography>

      {/* C · reading key */}
      <SectionTitle>{t('recap.reading.title')}</SectionTitle>
      <Typography variant="body1">
        {report.readingKeys.map((s) => renderReadingSentence(s, t)).join(' ')}
      </Typography>

      {/* D · three indicators */}
      <SectionTitle>{t('recap.dimensions.title')}</SectionTitle>
      <Paper variant="outlined">
        {dims.map((d, i) => (
          <React.Fragment key={d}>
            {i > 0 && <Divider />}
            <Box display="flex" alignItems="center" gap={2} paddingX={2} paddingY={1.5}>
              <Box flex={1} minWidth={0}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t(`recap.dimensions.${d}`)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`recap.metric.${d}`)}
                </Typography>
              </Box>
              <Chip
                label={t(`recap.level.${report.indicators[d]}`)}
                color={LEVEL_COLOR[report.indicators[d]]}
                sx={{ ...chipSx, width: 140 }}
              />
            </Box>
          </React.Fragment>
        ))}
      </Paper>

      {/* E · warnings — the whole section is absent when none is active */}
      {report.warnings.length > 0 && (
        <>
          <SectionTitle>{t('recap.warning.title')}</SectionTitle>
          {mainWarnings.map((w) => (
            <WarningBox key={w.id} warning={w} />
          ))}
          {otherWarnings.length > 0 && (
            <>
              <Collapse in={showAllWarnings}>
                {otherWarnings.map((w) => (
                  <WarningBox key={w.id} warning={w} />
                ))}
              </Collapse>
              <Button size="small" onClick={() => setShowAllWarnings((v) => !v)}>
                {showAllWarnings
                  ? t('recap.warning.show_less')
                  : t('recap.warning.show_more', { count: otherWarnings.length })}
              </Button>
            </>
          )}
        </>
      )}

      {/* F · interpretation */}
      <SectionTitle>{t('recap.final.title')}</SectionTitle>
      <Typography variant="body1">
        {report.finalKeys.map((k) => t(`recap.final.${k}`)).join(' ')}
      </Typography>

      {/* G · note */}
      <Typography variant="body2" fontStyle="italic" color="text.secondary" paddingTop={3}>
        {t('recap.note')}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {t('recap.purpose.label')}: {t(`recap.purpose.${report.purpose}`)} ·{' '}
        {t('recap.version', {
          analysis: report.versions.analysis,
          rules: report.versions.rules,
          prompt: report.versions.prompt,
        })}
      </Typography>
    </Box>
  );
};
